// context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import server from '../services/server';
import { useNavigate } from 'react-router-dom';

// Cria o contexto de autenticação
const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    // Estados principais do contexto
    const [authenticated, setAuthenticated] = useState(false); // Controla se o usuário está autenticado
    const [user, setUser] = useState(null); // Armazena os dados do usuário logado
    const [sessionChecked, setSessionChecked] = useState(false); // Flag para controlar se a sessão já foi verificada
    const [isLoading, setIsLoading] = useState(true); // Controla se está carregando a verificação inicial
    const navigate = useNavigate();

    // Função para verificar se existe uma sessão válida no servidor
    const verifySession = async () => {
        // Se já foi verificado, não verifica novamente
        if (sessionChecked) {
            return;
        }
        try {
            setIsLoading(true);
            // Faz requisição para o endpoint /auth para verificar se há token válido
            const { data } = await server.get('/auth');
            // Se chegou aqui, a sessão é válida
            setUser(data);
            setAuthenticated(true);
        } catch (err) {
            // Se deu erro, não há sessão válida
            setUser(null);
            setAuthenticated(false);
        } finally {
            // Sempre marca que a sessão foi verificada e para o loading
            setSessionChecked(true);
            setIsLoading(false);
        }
    };

    // useEffect que roda APENAS UMA VEZ quando o componente monta
    // Responsável por verificar se há uma sessão válida ao carregar a aplicação
    useEffect(() => {
        if(!Boolean(user)){
            verifySession();

        }
    }, []); // Array vazio = executa apenas uma vez

    // Função para fazer logout do usuário
    const logout = async () => {
        try {
            // Chama o endpoint de logout no servidor para invalidar o token
            await server.post('/auth/logout');
        } finally {
            // Limpa o estado local independente do resultado da requisição
            setUser(null);
            setAuthenticated(false);
            setSessionChecked(true);
            setIsLoading(false);
            // Redireciona para a página inicial
            navigate('/', { replace: true });
        }
    };

    // Função para fazer login do usuário (usada pelo AuthRedirect)
    const Login = ({ user }) => {
        setUser(user);
        setAuthenticated(true);
        setSessionChecked(true);
        setIsLoading(false);
    };

    // Retorna o provider com todos os valores e funções do contexto
    return (
        <AuthContext.Provider
            value={{ 
                authenticated, // Se o usuário está logado
                user, // Dados do usuário
                logout, // Função para fazer logout
                verifySession, // Função para verificar sessão
                Login, // Função para fazer login
                sessionChecked, // Se a sessão já foi verificada
                isLoading // Se está carregando a verificação inicial
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Hook personalizado para usar o contexto de autenticação
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth precisa estar dentro do AuthProvider');
    return context;
};
